// addOrderBy — VQE-Edit edit-action: append (or insert at index) an ORDER BY
// clause entry via SR-Edit's `add-order-by` op. SR-Edit synthesizes the
// `ORDER BY` keyword when the list is empty and inserts it at the right
// clause-tail offset (after FROM/WHERE/GROUP BY, before LIMIT/OFFSET) — this
// module just builds the StructuralEdit and delegates to `commitEdit`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove ORDER BY.

import { commitEdit, type CommitEditCtx } from '../commitEdit';

export type OrderByDirection = 'asc' | 'desc' | null;
export type OrderByNulls = 'first' | 'last' | null;

export interface AddOrderByOptions {
  /** Append direction keyword (uppercased: ASC / DESC). Omit for default. */
  readonly direction?: OrderByDirection;
  /** Append NULLS FIRST / NULLS LAST. Caller must pre-gate by dialect. */
  readonly nulls?: OrderByNulls;
  /** Insert AFTER this 0-based index in the existing ORDER BY list. */
  readonly afterIndex?: number;
}

/**
 * Format an ORDER BY clause entry from `(expression, direction, nulls)`.
 * Trims leading/trailing whitespace from `expression`. Returns the canonical
 * form `"<expression>[ ASC|DESC][ NULLS FIRST|NULLS LAST]"`.
 */
export function formatOrderByClauseText(
  expression: string,
  direction: OrderByDirection = null,
  nulls: OrderByNulls = null,
): string {
  const expr = expression.trim();
  if (expr.length === 0) {
    throw new Error('formatOrderByClauseText: expression must not be empty');
  }
  const parts = [expr];
  if (direction === 'asc') parts.push('ASC');
  else if (direction === 'desc') parts.push('DESC');
  if (nulls === 'first') parts.push('NULLS FIRST');
  else if (nulls === 'last') parts.push('NULLS LAST');
  return parts.join(' ');
}

/**
 * Append an ORDER BY entry derived from a column/expression text + optional
 * direction + optional NULLS FIRST/LAST keyword. The caller is responsible for
 * gating NULLS FIRST/LAST by dialect (postgres / oracle / sqlite >= 3.30 only).
 */
export async function addOrderBy(
  expression: string,
  ctx: CommitEditCtx,
  options: AddOrderByOptions = {},
): Promise<void> {
  const clauseText = formatOrderByClauseText(
    expression,
    options.direction ?? null,
    options.nulls ?? null,
  );
  await addOrderByClause(clauseText, ctx, { afterIndex: options.afterIndex });
}

/**
 * Append a pre-formatted ORDER BY clause entry. Use this when the popover or
 * editable pill produced the textual form directly (e.g. an expression that
 * already includes a function call or an alias-qualified column ref).
 */
export async function addOrderByClause(
  clauseText: string,
  ctx: CommitEditCtx,
  options: { readonly afterIndex?: number } = {},
): Promise<void> {
  const text = clauseText.trim();
  if (text.length === 0) {
    throw new Error('addOrderByClause: clauseText must not be empty');
  }
  await commitEdit(
    {
      op: 'add-order-by',
      clauseText: text,
      ...(options.afterIndex !== undefined ? { afterIndex: options.afterIndex } : {}),
    },
    ctx,
  );
}
